/**
 * detection — quelle langue afficher, et dans quel ordre on se pose la question.
 *
 * Six sources, de la plus explicite à la plus supposée. La première qui rend
 * une langue DISPONIBLE gagne ; une langue déclarée mais pas encore traduite ne
 * compte pas, sinon un lien `?lang=ar` livrerait une interface française sous
 * un drapeau arabe.
 *
 *   1. l'adresse (`?lang=`)   — un lien de courriel ou de partage porte sa langue ;
 *   2. le compte              — la préférence enregistrée, seule à traverser les appareils ;
 *   3. le stockage local      — ce que le visiteur sans compte a choisi la dernière fois ;
 *   4. le domaine             — `1cours1bot.fr` est le site français et ne sert que lui ;
 *   5. le navigateur          — `navigator.languages`, l'hypothèse la moins mauvaise ;
 *   6. le français            — le repli, toujours complet.
 *
 * Le domaine passe APRÈS les trois choix explicites et AVANT le navigateur :
 * il est une supposition sur qui arrive, comme `navigator.languages`, mais
 * mieux informée. Un enseignant qui a réglé son compte en arabe garde donc sa
 * langue sur le site français ; un visiteur au navigateur allemand, non.
 *
 * Le compte passe AVANT le stockage local : c'est le seul choix qui suive
 * l'usager d'un poste à l'autre, et le stockage local garde souvent la trace
 * d'un essai fait avant de se connecter.
 *
 * L'adresse passe avant le compte, parce qu'elle est le geste le plus récent :
 * un enseignant qui envoie à un élève un lien `?lang=es` veut cette langue-là,
 * quel que soit le réglage du compte qui ouvrira le lien.
 *
 * Le stockage passe par `safeStorage` — le seul chemin autorisé vers
 * localStorage dans ce dépôt, cf. CLAUDE.md. i18next sait persister tout seul,
 * mais il écrit dans `localStorage` en direct : sur un WebView Android sans
 * stockage, `localStorage` vaut `null` et l'accès lève.
 */

import { safeLocalStorage } from '../utils/safeStorage';
import { siteMonolingue } from './domaine';
import { estLangueDisponible, LANGUE_PAR_DEFAUT } from './languesInterface';

/** La clé de stockage. Versionnée : la sémantique d'une préférence peut changer. */
export const CLE_STOCKAGE = 'langueInterface_v1';

/** Le nom du paramètre d'adresse. Il est aussi lu par les liens des courriels. */
export const PARAM_URL = 'lang';

/**
 * Ramène un code quelconque à une langue disponible, ou `null`.
 *
 * Accepte les formes que rendent les navigateurs (`fr-FR`, `ar-MA`, `EN`) et
 * n'en garde que la sous-balise de langue : on ne distingue pas les régions,
 * une seule traduction sert `es-ES` et `es-AR`.
 */
export const normaliserCode = (brut) => {
    if (!brut || typeof brut !== 'string') return null;
    const code = brut.trim().toLowerCase().split(/[-_]/)[0];
    return estLangueDisponible(code) ? code : null;
};

/** 1. L'adresse. */
const depuisAdresse = () => {
    try {
        const params = new URLSearchParams(window.location.search);
        return normaliserCode(params.get(PARAM_URL));
    } catch (e) {
        return null;
    }
};

/** 3. Le stockage local. */
export const langueMemorisee = () => normaliserCode(safeLocalStorage.getItem(CLE_STOCKAGE));

/** Mémorise un choix. Rend `false` si le stockage n'a pas voulu — sans conséquence. */
export const memoriserLangue = (code) => safeLocalStorage.setItem(CLE_STOCKAGE, code);

/**
 * 4. Le domaine. Le site français ne sert que le français.
 *
 * Rend `null` sur le site international, pour laisser la main au navigateur :
 * là-bas, deviner reste la meilleure ouverture, et le sélecteur est affiché
 * pour corriger la devinette.
 */
const depuisDomaine = () => (siteMonolingue() ? LANGUE_PAR_DEFAUT : null);

/** 5. Le navigateur. On parcourt les préférences dans l'ordre déclaré par l'usager. */
const depuisNavigateur = () => {
    try {
        const listee = Array.isArray(navigator.languages) && navigator.languages.length
            ? navigator.languages
            : [navigator.language];
        for (const brut of listee) {
            const code = normaliserCode(brut);
            if (code) return code;
        }
    } catch (e) {
        // navigator absent (rendu hors navigateur) : on tombera sur le repli.
    }
    return null;
};

/**
 * La langue à afficher au démarrage.
 *
 * @param {string|null} langueDuCompte - la préférence enregistrée, si la session
 *   est déjà connue. Au tout premier rendu elle ne l'est pas : on passe `null`,
 *   et `AppI18nProvider` rappellera `appliquerLangue()` dès que le compte répond.
 */
export const resoudreLangue = (langueDuCompte = null) =>
    depuisAdresse()
    || normaliserCode(langueDuCompte)
    || langueMemorisee()
    || depuisDomaine()
    || depuisNavigateur()
    || LANGUE_PAR_DEFAUT;
